import {
  InfiniteData,
  useInfiniteQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { gqlClient } from '@/api/graphql-client';
import { getNextPageParamFromPaginatedResult } from '@/api/infiniteQueries';
import projectKeys from '@/api/queries/project/keyFactory';
import { graphql } from '@/gql';
import {
  GetPaginatedActivityFeedForProjectInput,
  GetProjectActivityFeedQuery,
} from '@/gql/graphql';
import { PAGINATION_ARG_DEFAULTS } from '@payaca/shared-isomorphic/constants/graphql-pagination';

const GetProjectActivityFeed = graphql(`
  query GetProjectActivityFeed(
    $projectId: ID!
    $input: GetPaginatedActivityFeedForProjectInput!
    $pagination: PaginationInput!
  ) {
    projectById(id: $projectId) {
      activityFeed(pagination: $pagination, input: $input) {
        items {
          ... on ProjectActivityEvent {
            __typename
            description
            id
            name
            timestamp
            type
            linkedEntity {
              ... on Note {
                id
              }
            }
          }
          ... on IntegratedEmailProjectActivityEvent {
            __typename
            description
            id
            name
            timestamp
            type
            integratedEmailId
          }
        }
        limit
        offset
        totalCount
      }
    }
  }
`);

const useGetProjectActivityFeed = (
  projectId?: string,
  input?: GetPaginatedActivityFeedForProjectInput
) => {
  const queryClient = useQueryClient();

  const queryFn = ({ pageParam }: { pageParam: number }) => {
    return gqlClient.request(GetProjectActivityFeed, {
      projectId: projectId!,
      pagination: {
        offset: pageParam,
        limit: PAGINATION_ARG_DEFAULTS.limit,
      },
      input: input || {},
    });
  };

  return useInfiniteQuery({
    queryKey: projectKeys.activityFeed(projectId!, input),
    queryFn,
    initialPageParam: PAGINATION_ARG_DEFAULTS.offset,
    enabled: !!projectId,
    // If `input` contains the `types` filter
    // Check that all the unfiltered data has already been fetched
    // if so, filter it client-side before fetching it again from the server
    initialData: () => {
      if (!input || !input.types || !input.types.length) {
        return;
      }

      const data = queryClient.getQueryData<
        InfiniteData<GetProjectActivityFeedQuery, number>
      >(
        projectKeys.activityFeed(projectId!, {
          types: [],
        })
      );

      if (!data) {
        return;
      }

      // We already have all the data client-side
      return {
        ...data,
        pages: data.pages.map((page) => ({
          ...page,
          projectById: {
            ...page.projectById,
            activityFeed: page.projectById.activityFeed
              ? {
                  ...page.projectById.activityFeed,
                  // ... so filter it client-side
                  items: page.projectById.activityFeed.items.filter((item) =>
                    input.types!.includes(item.type)
                  ),
                }
              : undefined,
          },
        })),
      };
    },
    getNextPageParam: getNextPageParamFromPaginatedResult(
      (lastPage) => lastPage.projectById.activityFeed
    ),
  });
};

export default useGetProjectActivityFeed;
